import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "date-range-filter" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = {
  key: 0,
  class: "date-range-filter__dropdown"
}
const _hoisted_4 = { class: "date-range-filter__dropdown-content" }
const _hoisted_5 = { class: "columns" }
const _hoisted_6 = { class: "column is-narrow date-range-filter__presets" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "column is-flex is-flex-direction-column is-justify-content-space-between" }
const _hoisted_10 = { class: "date-range-filter__date-picker" }
const _hoisted_11 = { class: "date-range-filter__actions" }

import { DatePicker, FluentDate } from '@/features/reports/services';
import { SET_REPORTING_DATE_RANGE } from '@/store/reports/mutationTypes';
import { DateGrouping, DateOption } from '@/features/reports/models';
import moment from 'moment';
import { useStore } from 'vuex';
import { computed, onMounted, ref, watch } from 'vue';
// Remove this line: import VcDatePicker from 'v-calendar';


export default /*@__PURE__*/_defineComponent({
  __name: 'DateRangeFilter',
  setup(__props) {

const store = useStore();
const selectedDateOption = computed<DateOption>(() => store.getters.selectedDateOption);
const isReportLoading = computed<boolean>(() => store.getters.isReportLoading);
const setReportingDateRange = (dateOption: DateOption) => store.commit(SET_REPORTING_DATE_RANGE, dateOption);

// Use a generic Ref type or infer from the component instance
const calendar = ref<InstanceType<any> | null>(null); // Fallback type; refine if needed

const dateSelectorOpen = ref(false);
const value = ref<DateOption>(DatePicker.options.today);
const selectedDateRange = ref(DatePicker.options.today.getDateRange());

const datePickerModelConfig = ref({
  start: {
    timeAdjust: '00:00:00',
  },
  end: {
    timeAdjust: '23:59:59',
  },
});

const availableDates = {
  start: new FluentDate(new Date(2018, 0, 1)).startOfYear().value,
  end: new FluentDate().endOfYear().value,
};

const datePickerAttributes = [
  {
    key: 'today',
    highlight: {
      fillMode: 'outline',
    },
    dates: new Date(),
  },
];

onMounted(() => {
  if (!isSelectedDateInOptions(value.value)) {
    const val = getSelectedPreset() ?? getDateOptionForCustomRange();
    setReportingDateRange(val);
  }
});

watch(
  selectedDateOption,
  () => {
    value.value = selectedDateOption.value;
    selectedDateRange.value = selectedDateOption.value.getDateRange();
    moveCalendar();
  },
  { immediate: true },
);

function handleDateChange(changedValue: any): void {
  const selectedPreset = getSelectedPreset();
  if (selectedPreset) {
    value.value = selectedPreset;
    return;
  }
  value.value = getDateOptionForCustomRange();
}

function isSelectedDateInOptions(value: DateOption) {
  return dateOptions.value.some((option) => option.label === value.label);
}

const dateOptions = computed(() => DatePicker.options.toArray());

function getSelectedPreset(): DateOption | undefined {
  const matchingValues = DatePicker.options.toArray().filter((opt: DateOption) => {
    const optRange = opt.getDateRange();
    return (
      moment(selectedDateRange.value.start).isSame(optRange.start, 'day') &&
      moment(selectedDateRange.value.end).isSame(optRange.end, 'day')
    );
  });
  const matchingValueWithLabel = matchingValues.find((opt: DateOption) => {
    return opt.label === value.value.label;
  });
  return matchingValueWithLabel ? matchingValueWithLabel : matchingValues[0];
}

function getDateOptionForCustomRange(): DateOption {
  const selectedStart = selectedDateRange.value.start;
  const selectedEnd = selectedDateRange.value.end;
  return {
    group: getGroupingForCustomRange(),
    getDateRange: () => ({
      start: selectedStart,
      end: selectedEnd,
    }),
    label: generateLabelForCustomRange(),
    listGroup: 0,
  };
}

function cancel(): void {
  value.value = selectedDateOption.value;
  selectedDateRange.value = selectedDateOption.value.getDateRange();
  dateSelectorOpen.value = false;
}

function toggleDateSelector(): void {
  if (dateSelectorOpen.value) {
    cancel();
  } else {
    dateSelectorOpen.value = true;
  }
}

function isActivePreset(label?: string): boolean {
  if (!label) {
    return !DatePicker.options.toArray().some((opt: DateOption) => opt.label === value.value.label);
  }
  return value.value.label === label;
}

function setDateOption(dateRange: DateOption): void {
  if (calendar.value) {
    calendar.value.dragValue = null;
  }
  value.value = dateRange;
  selectedDateRange.value = dateRange.getDateRange();
  moveCalendar();
}

function applySelection(): void {
  setReportingDateRange(value.value);
  toggleDateSelector();
}

function shouldDisplayPresetDivider(index: number): boolean {
  if (index === 0) {
    return false;
  }
  return dateOptions.value[index].listGroup > dateOptions.value[index - 1].listGroup;
}

function getDaysBetweenDates(date1: Date, date2: Date): number {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay));
}

function generateLabelForCustomRange(): string {
  if (startDateEqualsEndDate()) {
    return moment(selectedDateRange.value.start).utc().format('MMM D, YYYY');
  }
  return `${moment(selectedDateRange.value.start).utc().format('MMM D, YYYY')} - ${moment(selectedDateRange.value.end)
    .utc()
    .format('MMM D, YYYY')}`;
}

function startDateEqualsEndDate() {
  return getDaysBetweenDates(selectedDateRange.value.start, selectedDateRange.value.end) <= 1;
}

function moveCalendar(): void {
  selectedDateRange.value = value.value.getDateRange();
  if (calendar.value) {
    calendar.value.move(selectedDateRange.value.start, {
      position: 1,
      transition: 'slide-h',
    });
  }
}

function getGroupingForCustomRange(): DateGrouping {
  const daySpan = getDaysBetweenDates(selectedDateRange.value.start, selectedDateRange.value.end);
  if (daySpan === 1) {
    return DateGrouping.HOUR;
  }
  return daySpan < 91 ? DateGrouping.DAY : DateGrouping.MONTH;
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_vc_date_picker = _resolveComponent("vc-date-picker")!
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_button_group = _resolveComponent("n-button-group")!
  const _directive_click_away = _resolveDirective("click-away")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      class: "date-range-filter__trigger button",
      disabled: isReportLoading.value,
      onClick: toggleDateSelector
    }, [
      _createElementVNode("div", null, [
        _createVNode(_component_font_awesome_icon, {
          class: "date-range-filter__trigger-icon",
          icon: "calendar-alt"
        })
      ]),
      _createElementVNode("div", null, [
        _createElementVNode("span", null, _toDisplayString(selectedDateOption.value.label), 1)
      ])
    ], 8, _hoisted_2),
    (dateSelectorOpen.value)
      ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dateOptions.value, (date, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: date.label,
                      class: "date-range-filter__preset-container"
                    }, [
                      (shouldDisplayPresetDivider(index))
                        ? (_openBlock(), _createElementBlock("hr", _hoisted_7))
                        : _createCommentVNode("", true),
                      (_openBlock(), _createElementBlock("a", {
                        key: date.label,
                        onClick: ($event: any) => (setDateOption(date)),
                        class: _normalizeClass(["date-range-filter__preset", { 'date-range-filter__preset--active': isActivePreset(date.label) }])
                      }, _toDisplayString(date.label), 11, _hoisted_8))
                    ]))
                  }), 128))
                ])
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(_component_vc_date_picker, {
                    modelValue: selectedDateRange.value,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event: any) => ((selectedDateRange).value = $event)),
                      handleDateChange
                    ],
                    modelModifiers: { range: true },
                    ref_key: "calendar",
                    ref: calendar,
                    columns: 2,
                    range: "",
                    borderless: "",
                    "is-required": "",
                    transparent: "",
                    "model-config": datePickerModelConfig.value,
                    timezone: "UTC",
                    attributes: datePickerAttributes,
                    "available-dates": availableDates
                  }, null, 8, ["modelValue", "model-config"])
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_component_n_button_group, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_n_button, {
                        onClick: applySelection,
                        variant: "primary"
                      }, {
                        default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                          _createTextVNode("Apply", -1)
                        ]))]),
                        _: 1
                      }),
                      _createVNode(_component_n_button, {
                        onClick: cancel,
                        variant: "primary--outline"
                      }, {
                        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                          _createTextVNode("Cancel", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ])
              ])
            ])
          ])
        ])), [
          [_directive_click_away, cancel]
        ])
      : _createCommentVNode("", true)
  ]))
}
}

})